import React, { useState, useEffect, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  TextField,
  MenuItem,
  Link,
  Box,
} from '@material-ui/core';
import axios from 'axios';
import nunjucks from 'nunjucks';
import yaml from 'js-yaml';
import debounce from 'lodash.debounce';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

// Configure nunjucks with custom delimiters.
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

interface NewOauthData {
  AUTHORIZER_AUDIENCE: string;
  AUTHORIZER_TOKEN_ISSUER: string;
  SWAGGER_AUTH_CLIENT_ID: string;
  SWAGGER_AUTH_CLIENT_SECRET: string;
  SWAGGER_AUTH_ISSUER: string;
  SWAGGER_AUTH_SCOPE: string;
  SWAGGER_BASE_URL: string;
}

type OauthMode = 'existing' | 'create';

interface FinalOauthData {
  oauthRequired: boolean;
  mode?: OauthMode;
  existingProvider?: string;
  newOauth?: NewOauthData;
}

const defaultOauthData: NewOauthData = {
  AUTHORIZER_AUDIENCE: '',
  AUTHORIZER_TOKEN_ISSUER: '',
  SWAGGER_AUTH_CLIENT_ID: '',
  SWAGGER_AUTH_CLIENT_SECRET: '',
  SWAGGER_AUTH_ISSUER: '',
  SWAGGER_AUTH_SCOPE: '',
  SWAGGER_BASE_URL: '',
};

const defaultFinalOauthData: FinalOauthData = {
  oauthRequired: false,
  mode: 'existing',
  existingProvider: '',
  newOauth: { ...defaultOauthData },
};

export const OauthForApisField = (props: FieldExtensionComponentProps<FinalOauthData>) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;

  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [baseUrl, setBaseUrl] = useState<string>('');
  useEffect(() => {
    const fetchBaseUrl = async () => {
      const url = await discoveryApi.getBaseUrl('scaffolder');
      setBaseUrl(url);
    };
    fetchBaseUrl();
  }, [discoveryApi]);

  // Resolve adoProject using nunjucks.
  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';
  const resolvedAdoProject = nunjucks.renderString(adoProjectTemplate, formContext.formData).trim();
  console.log('OauthForApisField: resolved adoProject =', resolvedAdoProject);

  // Initialize local state from formData (or use defaults if formData is empty).
  const initialData: FinalOauthData = 
    formData && typeof formData === 'object' && Object.keys(formData).length > 0
      ? formData
      : defaultFinalOauthData;

  const [useOauth, setUseOauth] = useState<boolean>(initialData.oauthRequired);
  const [oauthMode, setOauthMode] = useState<OauthMode>(initialData.mode || 'existing');
  const [selectedProvider, setSelectedProvider] = useState<string>(initialData.existingProvider || '');
  const [newOauthData, setNewOauthData] = useState<NewOauthData>(initialData.newOauth || { ...defaultOauthData });

  // On mount, if formData is empty (or has no keys), force default values.
  useEffect(() => {
    if (!formData || (typeof formData === 'object' && Object.keys(formData).length === 0)) {
      onChange(defaultFinalOauthData);
    }
  }, [formData, onChange]);

  // A function to sync local state to parent.
  const syncToParent = () => {
    const finalObj: FinalOauthData = useOauth
      ? oauthMode === 'existing'
        ? {
            oauthRequired: true,
            mode: 'existing',
            existingProvider: selectedProvider,
            newOauth: { ...defaultOauthData },
          }
        : {
            oauthRequired: true,
            mode: 'create',
            existingProvider: '',
            newOauth: { ...newOauthData },
          }
      : { ...defaultFinalOauthData };
    onChange(finalObj);
  };

  // Call syncToParent in every event handler.
  useEffect(() => {
    syncToParent();
  }, [useOauth, oauthMode, selectedProvider, newOauthData]);

  // Fetch provider options when in "existing" mode.
  const fetchProviders = async () => {
    if (!resolvedAdoProject || !baseUrl) return;
    setLoadingProviders(true);
    setProviderError('');
    try {
      const creds = await identityApi.getCredentials();
      const token = creds.token;
      const response = await axios.post(
        `${baseUrl}/aws/params`,
        { name: `/backstage/project/${resolvedAdoProject}` },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const rawValue = response.data.value || '';
      let authProviders: string[] = [];
      if (rawValue) {
        const parsed = yaml.load(rawValue);
        if (
          parsed &&
          typeof parsed === 'object' &&
          (parsed as any).auth_providers &&
          Array.isArray((parsed as any).auth_providers)
        ) {
          authProviders = (parsed as any).auth_providers;
        }
      }
      setProviderOptions(authProviders);
      if (authProviders.length > 0) {
        setSelectedProvider(prev => (prev ? prev : authProviders[0]));
      } else {
        setSelectedProvider('');
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviderError('Failed to fetch providers.');
      setProviderOptions([]);
      setSelectedProvider('');
    } finally {
      setLoadingProviders(false);
    }
  };

  const [providerOptions, setProviderOptions] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [providerError, setProviderError] = useState<string>('');

  const debouncedFetchProviders = useMemo(() => debounce(fetchProviders, 500), [resolvedAdoProject, baseUrl]);

  useEffect(() => {
    if (useOauth && oauthMode === 'existing') {
      debouncedFetchProviders();
    }
  }, [useOauth, oauthMode, debouncedFetchProviders]);

  const handleCreateDemoOauth = async () => {
    if (!resolvedAdoProject || !baseUrl) return;
    try {
      const creds = await identityApi.getCredentials();
      const token = creds.token;
      const response = await axios.post(
        `${baseUrl}/create/oauth-demo`,
        { projectName: resolvedAdoProject },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        fetchProviders();
      }
    } catch (error) {
      console.error('Error creating demo oauth:', error);
    }
  };

  const handleUseOauthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseOauth(event.target.checked);
  };

  const handleOauthModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOauthMode(event.target.value as OauthMode);
  };

  const handleExistingProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProvider(event.target.value);
  };

  const handleNewOauthDataChange = (field: keyof NewOauthData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewOauthData(prev => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <FormControl component="fieldset" fullWidth>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={useOauth} onChange={handleUseOauthChange} />}
          label="Use oauth for apis?"
        />
      </FormGroup>

      {useOauth && (
        <Box ml={2} mt={1}>
          <FormLabel component="legend">OAuth Mode</FormLabel>
          <RadioGroup value={oauthMode} onChange={handleOauthModeChange} row>
            <FormControlLabel value="existing" control={<Radio />} label="Use Existing" />
            <FormControlLabel value="create" control={<Radio />} label="Create New" />
          </RadioGroup>

          {oauthMode === 'existing' && (
            <Box ml={2} mt={1}>
              <FormLabel component="legend">Existing OAuth Provider</FormLabel>
              {loadingProviders ? (
                <div>Loading providers...</div>
              ) : providerOptions.length > 0 ? (
                <TextField
                  select
                  fullWidth
                  value={selectedProvider}
                  onChange={handleExistingProviderChange}
                  variant="outlined"
                  size="small"
                >
                  {providerOptions.map(provider => (
                    <MenuItem key={provider} value={provider}>
                      {provider}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Box>
                  <div>No providers found.</div>
                  <Link component="button" onClick={handleCreateDemoOauth}>
                    Create a demo oauth (backstage-demo)
                  </Link>
                </Box>
              )}
            </Box>
          )}

          {oauthMode === 'create' && (
            <Box ml={2} mt={1}>
              <FormLabel component="legend">New OAuth Configuration</FormLabel>
              <TextField
                label="AUTHORIZER_AUDIENCE"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.AUTHORIZER_AUDIENCE}
                onChange={handleNewOauthDataChange('AUTHORIZER_AUDIENCE')}
              />
              <TextField
                label="AUTHORIZER_TOKEN_ISSUER"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.AUTHORIZER_TOKEN_ISSUER}
                onChange={handleNewOauthDataChange('AUTHORIZER_TOKEN_ISSUER')}
              />
              <TextField
                label="SWAGGER_AUTH_CLIENT_ID"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.SWAGGER_AUTH_CLIENT_ID}
                onChange={handleNewOauthDataChange('SWAGGER_AUTH_CLIENT_ID')}
              />
              <TextField
                label="SWAGGER_AUTH_CLIENT_SECRET"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.SWAGGER_AUTH_CLIENT_SECRET}
                onChange={handleNewOauthDataChange('SWAGGER_AUTH_CLIENT_SECRET')}
              />
              <TextField
                label="SWAGGER_AUTH_ISSUER"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.SWAGGER_AUTH_ISSUER}
                onChange={handleNewOauthDataChange('SWAGGER_AUTH_ISSUER')}
              />
              <TextField
                label="SWAGGER_AUTH_SCOPE"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.SWAGGER_AUTH_SCOPE}
                onChange={handleNewOauthDataChange('SWAGGER_AUTH_SCOPE')}
              />
              <TextField
                label="SWAGGER_BASE_URL"
                fullWidth
                variant="outlined"
                margin="normal"
                value={newOauthData.SWAGGER_BASE_URL}
                onChange={handleNewOauthDataChange('SWAGGER_BASE_URL')}
              />
            </Box>
          )}
        </Box>
      )}

      <FormHelperText>
        {rawErrors && rawErrors.length > 0
          ? rawErrors.join(', ')
          : schema.description || ''}
      </FormHelperText>
    </FormControl>
  );
};
